'use client';
import React, { useState } from 'react';
import { useSession, signOut } from 'next-auth/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { 
  FaBars, 
  FaHome, 
  FaUsers, 
  FaUserPlus, 
  FaTable, 
  FaIdCard, 
  FaClock, 
  FaEnvelope, 
  FaChurch, 
  FaHeart, 
  FaUser, 
  FaCog, 
  FaSignOutAlt,
  FaChevronDown,
  FaPlus
} from 'react-icons/fa';

export default function Sidebar({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPeopleExpanded, setIsPeopleExpanded] = useState(true);
  const { status, data: session } = useSession();
  const pathname = usePathname();
  
  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const isActive = (path) => pathname === path || pathname?.startsWith(path + '/');

  // For unauthenticated users, just render children
  if (status !== 'authenticated') {
    return <>{children}</>;
  }

  return (
    <div className="drawer lg:drawer-open">
      <input id="drawer-toggle" type="checkbox" className="drawer-toggle" checked={isOpen} onChange={() => setIsOpen(!isOpen)} />
      
      {/* Mobile header */}
      <div className="drawer-content flex flex-col">
        <div className="navbar lg:hidden bg-base-100 border-b border-base-200">
          <div className="flex-none">
            <label htmlFor="drawer-toggle" className="btn btn-square btn-ghost">
              <FaBars className="text-lg" />
            </label>
          </div>
          <div className="flex-1">
            <span className="text-lg font-semibold">Shepherd Sheep</span>
          </div>
        </div>
        
        <main className="flex-1 p-4 lg:p-6 bg-base-100 min-h-screen">
          {children}
        </main>
      </div>

      {/* Sidebar */}
      <div className="drawer-side z-50">
        <label htmlFor="drawer-toggle" className="drawer-overlay"></label>
        <aside className="min-h-full w-64 bg-base-200 flex flex-col">
          {/* Logo Header */}
          <div className="p-5 border-b border-base-300">
            <Link href="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white">
                <FaUsers className="text-lg" />
              </div>
              <div>
                <h1 className="font-bold text-lg leading-tight">Shepherd Sheep</h1>
                <p className="text-xs text-base-content/50">Never Walk Alone</p>
              </div>
            </Link>
          </div>

          {/* Quick Add Button */}
          <div className="px-4 py-3">
            <Link 
              href="/people/create" 
              onClick={handleLinkClick}
              className="btn btn-primary btn-block gap-2 shadow-sm"
            >
              <FaPlus className="text-sm" />
              Add Person
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-2 overflow-y-auto">
            <div className="space-y-1">
              {/* Dashboard */}
              <NavLink href="/" icon={FaHome} active={pathname === '/'} onClick={handleLinkClick}>
                Dashboard
              </NavLink>

              {/* People Management */}
              <div className="pt-4">
                <p className="px-3 text-xs font-medium text-base-content/40 uppercase tracking-wider mb-2">
                  People
                </p>
                <div className="space-y-1">
                  <NavLink 
                    href="/people/all" 
                    icon={FaTable} 
                    active={isActive('/people')}
                    onClick={handleLinkClick}
                  >
                    All People
                  </NavLink>
                  <NavLink 
                    href="/people/cards" 
                    icon={FaIdCard} 
                    active={pathname === '/people/cards'}
                    onClick={handleLinkClick}
                  >
                    Card View
                  </NavLink>
                  <NavLink 
                    href="/people/frequency" 
                    icon={FaClock} 
                    active={pathname === '/people/frequency'}
                    onClick={handleLinkClick}
                  >
                    By Frequency
                  </NavLink>
                </div>
              </div>

              {/* Categories */}
              <div className="pt-4">
                <p className="px-3 text-xs font-medium text-base-content/40 uppercase tracking-wider mb-2">
                  Categories
                </p>
                <div className="space-y-1">
                  <CategoryLink href="/people/category/bbcc" icon={FaChurch} onClick={handleLinkClick}>
                    BBCC
                  </CategoryLink>
                  <CategoryLink href="/people/category/goshen" icon={FaChurch} onClick={handleLinkClick}>
                    Goshen
                  </CategoryLink>
                  <CategoryLink href="/people/category/personal" icon={FaHeart} onClick={handleLinkClick}>
                    Personal
                  </CategoryLink>
                </div>
              </div>

              {/* Communication */}
              <div className="pt-4">
                <p className="px-3 text-xs font-medium text-base-content/40 uppercase tracking-wider mb-2">
                  Communication
                </p>
                <NavLink 
                  href="/messages" 
                  icon={FaEnvelope} 
                  active={isActive('/messages')}
                  onClick={handleLinkClick}
                >
                  Messages
                </NavLink>
              </div>
            </div>
          </nav>

          {/* User Section */}
          <div className="border-t border-base-300 p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="avatar">
                <div className="w-9 h-9 rounded-full bg-base-300 flex items-center justify-center overflow-hidden">
                  {session?.user?.image ? (
                    <Image
                      src={session.user.image}
                      alt={session.user.name || 'User'}
                      width={36}
                      height={36}
                      className="rounded-full"
                    />
                  ) : (
                    <FaUser className="text-base-content/50 text-sm" />
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{session?.user?.name}</p>
                <p className="text-xs text-base-content/50 truncate">{session?.user?.email}</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Link 
                href="/profile" 
                onClick={handleLinkClick}
                className="btn btn-sm btn-ghost flex-1 gap-1"
              >
                <FaCog className="text-xs" />
                Settings
              </Link>
              <button 
                onClick={() => signOut()} 
                className="btn btn-sm btn-ghost btn-error flex-1 gap-1"
              >
                <FaSignOutAlt className="text-xs" />
                Sign Out
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// Navigation Link Component
function NavLink({ href, icon: Icon, active, children, onClick }) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
        active 
          ? 'bg-primary text-primary-content font-medium' 
          : 'text-base-content/70 hover:bg-base-300 hover:text-base-content'
      }`}
    >
      <Icon className={`text-sm ${active ? 'text-primary-content' : 'text-base-content/50'}`} />
      {children}
    </Link>
  );
}

// Category Link Component
function CategoryLink({ href, icon: Icon, children, onClick }) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-base-content/70 hover:bg-base-300 hover:text-base-content transition-colors"
    >
      <Icon className="text-sm text-base-content/40" />
      {children}
    </Link>
  );
}
